import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { Navbar1 } from "@/components/ui/navbar-1";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { amIAdmin } from "@/lib/blog.functions";
import {
  listEmailTemplates,
  renderEmailPreview,
} from "@/lib/email-preview.functions";
import { sendTransactionalEmail } from "@/lib/email/send";

export const Route = createFileRoute("/_authenticated/admin/emails")({
  component: AdminEmailsPreview,
});

function AdminEmailsPreview() {
  const isAdminFn = useServerFn(amIAdmin);
  const listFn = useServerFn(listEmailTemplates);
  const renderFn = useServerFn(renderEmailPreview);

  const { data: isAdmin, isLoading: checking } = useQuery({
    queryKey: ["email-preview", "is-admin"],
    queryFn: () => isAdminFn(),
  });

  const { data: templates = [] } = useQuery({
    queryKey: ["email-preview", "templates"],
    queryFn: () => listFn(),
    enabled: !!isAdmin,
  });

  const [selected, setSelected] = useState<string | null>(null);
  const [dataJson, setDataJson] = useState<string>("{}");
  const [recipient, setRecipient] = useState<string>("");
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");

  const current = useMemo(
    () => templates.find((t) => t.name === selected) ?? null,
    [templates, selected],
  );

  // Auto-select first template + prefill data
  useEffect(() => {
    if (!selected && templates.length) {
      setSelected(templates[0].name);
    }
  }, [templates, selected]);

  useEffect(() => {
    if (current) setDataJson(current.previewDataJson);
  }, [current?.name]);

  // Prefill recipient with logged-in email
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user?.email && !recipient) setRecipient(data.user.email);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preview = useQuery({
    queryKey: ["email-preview", "render", selected, dataJson],
    queryFn: () =>
      renderFn({ data: { templateName: selected!, templateDataJson: dataJson } }),
    enabled: !!selected,
    retry: false,
  });

  const sendMut = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error("Selecione um template");
      if (!recipient) throw new Error("Informe um email");
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(dataJson || "{}");
      } catch {
        throw new Error("JSON inválido");
      }
      const res = await sendTransactionalEmail({
        templateName: selected,
        recipientEmail: recipient,
        idempotencyKey: `preview-${selected}-${Date.now()}`,
        templateData: parsed,
      });
      if (!res.success) throw new Error(res.error || "Falha ao enviar");
    },
    onSuccess: () => toast.success("Email de teste enfileirado"),
    onError: (e: any) => toast.error(e?.message ?? "Erro ao enviar"),
  });

  if (checking) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar1 />
        <div className="mx-auto max-w-4xl px-6 pt-28 text-sm text-muted-foreground">
          Verificando permissões…
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar1 />
        <div className="mx-auto max-w-4xl px-6 pt-28 text-sm text-muted-foreground">
          Acesso restrito a administradores.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar1 />
      <div className="mx-auto max-w-7xl px-4 pt-24 pb-16 sm:px-6">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold">Pré-visualização de emails</h1>
          <p className="text-sm text-muted-foreground">
            Renderize cada template com dados reais antes de disparar.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
          {/* Sidebar */}
          <div className="space-y-2">
            {templates.map((t) => (
              <button
                key={t.name}
                onClick={() => setSelected(t.name)}
                className={`w-full rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                  selected === t.name
                    ? "border-primary bg-primary/10"
                    : "border-border hover:bg-muted"
                }`}
              >
                <div className="font-medium">{t.displayName}</div>
                <div className="text-xs text-muted-foreground">{t.name}</div>
              </button>
            ))}
            {templates.length === 0 && (
              <div className="text-sm text-muted-foreground">
                Nenhum template registrado.
              </div>
            )}
          </div>

          {/* Main */}
          <div className="space-y-4">
            <Card>
              <CardContent className="space-y-4 pt-6">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">
                    Dados do template (JSON)
                  </label>
                  <Textarea
                    value={dataJson}
                    onChange={(e) => setDataJson(e.target.value)}
                    rows={8}
                    className="mt-1 font-mono text-xs"
                    spellCheck={false}
                  />
                </div>

                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  <div className="flex-1">
                    <label className="text-xs font-medium text-muted-foreground">
                      Enviar teste para
                    </label>
                    <Input
                      type="email"
                      value={recipient}
                      onChange={(e) => setRecipient(e.target.value)}
                      placeholder="seu@email.com"
                      className="mt-1"
                    />
                  </div>
                  <Button
                    onClick={() => sendMut.mutate()}
                    disabled={sendMut.isPending || !selected}
                  >
                    {sendMut.isPending ? "Enviando…" : "Enviar teste"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {preview.data?.subject || "—"}
                    </div>
                    <div className="text-xs text-muted-foreground">Assunto</div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant={device === "desktop" ? "default" : "outline"}
                      onClick={() => setDevice("desktop")}
                    >
                      Desktop
                    </Button>
                    <Button
                      size="sm"
                      variant={device === "mobile" ? "default" : "outline"}
                      onClick={() => setDevice("mobile")}
                    >
                      Mobile
                    </Button>
                  </div>
                </div>

                {preview.isLoading && (
                  <div className="py-12 text-center text-sm text-muted-foreground">
                    Renderizando…
                  </div>
                )}
                {preview.error && (
                  <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
                    {(preview.error as Error).message}
                  </div>
                )}
                {preview.data && (
                  <div className="flex justify-center">
                    <iframe
                      title="Email preview"
                      srcDoc={preview.data.html}
                      className="rounded-md border bg-white"
                      style={{
                        width: device === "mobile" ? 390 : "100%",
                        maxWidth: device === "mobile" ? 390 : 780,
                        height: 800,
                      }}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
